import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { WeekStreakTracker } from '@/components/molecules/WeekStreakTracker';
import { ErrorState } from '@/components/molecules/ErrorState';
import { usersAPI, type StudentObjectives } from '@/services/api/users.api';
import { ROUTES } from '@/constants/routes';
import { LearningStatsSection } from './components/LearningStatsSection';
import { PerformanceSection } from './components/PerformanceSection';
import { SkillBreakdownSection } from './components/SkillBreakdownSection';
import styles from './ProgressPage.module.scss';

/** `false`: oculta el desglose por habilidad en UI; el componente sigue en el árbol vía condicional (poner `true` para recuperarlo). */
const SHOW_SKILL_BREAKDOWN = false;

export function ProgressPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [objectives, setObjectives] = useState<StudentObjectives | null>(null);
  const [isLoadingObjectives, setIsLoadingObjectives] = useState(true);
  const [objectivesError, setObjectivesError] = useState(false);

  const loadProgressData = useCallback(
    async (silent: boolean) => {
      if (!user?.id) {
        setIsLoadingObjectives(false);
        return;
      }

      if (!silent) {
        setIsLoadingObjectives(true);
      }

      try {
        const objs = await usersAPI.getObjectives();
        setObjectives(objs);
        setObjectivesError(false);
      } catch (error) {
        console.error('Failed to load progress data:', error);
        if (!silent) {
          setObjectives(null);
          setObjectivesError(true);
        }
      } finally {
        if (!silent) {
          setIsLoadingObjectives(false);
        }
      }
    },
    [user?.id]
  );

  /** Carga inicial y cada vez que se vuelve a esta ruta (p. ej. tras un reto). */
  useEffect(() => {
    if (location.pathname !== ROUTES.STUDENT.PROGRESS) return;
    void loadProgressData(false);
  }, [user?.id, location.pathname, location.key, loadProgressData]);

  /** Al volver a la pestaña del navegador, actualizar sin pantallas de carga completas. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !user?.id) return;
      if (location.pathname !== ROUTES.STUDENT.PROGRESS) return;
      void loadProgressData(true);
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?.id, location.pathname, loadProgressData]);

  return (
    <div className={styles.progress}>
      <div className={styles.progress__header}>
        <div>
          <h1 className={styles.progress__title}>{t('progressPage.title')}</h1>
          <p className={styles.progress__subtitle}>{t('progressPage.subtitle')}</p>
        </div>
      </div>

      <div className={styles.progress__topCluster}>
        <WeekStreakTracker />
        <LearningStatsSection />
        {objectivesError && !isLoadingObjectives ? (
          <ErrorState onRetry={() => void loadProgressData(false)} />
        ) : (
          <PerformanceSection objectives={objectives} isLoading={isLoadingObjectives} />
        )}
      </div>

      {SHOW_SKILL_BREAKDOWN ? <SkillBreakdownSection /> : null}
    </div>
  );
}

export default ProgressPage;
